import { useState, useCallback, useEffect } from 'react';

interface ValidationRule<T> {
  field: keyof T;
  validate: (value: T[keyof T], formData: T) => boolean;
  errorMessage: string;
}

interface UseFormStateOptions<T> {
  initialData: T;
  validationRules?: ValidationRule<T>[];
  resetOnOpen?: boolean;
  openTrigger?: boolean;
}

interface FormStateResult<T> {
  formData: T;
  errors: Partial<Record<keyof T, string>>;
  isValid: boolean;
  isDirty: boolean;
  updateField: <K extends keyof T>(field: K, value: T[K]) => void;
  updateFields: (updates: Partial<T>) => void;
  validateForm: () => boolean;
  validateField: (field: keyof T) => boolean;
  resetForm: (newInitialData?: Partial<T>) => void;
  setFormData: (data: T) => void;
  clearErrors: () => void;
  setError: (field: keyof T, error: string) => void;
}

export function useFormState<T extends Record<string, unknown> | object>(
  options: UseFormStateOptions<T>
): FormStateResult<T> {
  const { initialData, validationRules = [], resetOnOpen = false, openTrigger } = options;

  const [formData, setFormData] = useState<T>(initialData);
  const [errors, setErrors] = useState<Partial<Record<keyof T, string>>>({});
  const [isDirty, setIsDirty] = useState(false);

  const resetForm = useCallback(
    (newInitialData?: Partial<T>) => {
      const resetData = {
        ...initialData,
        ...newInitialData,
      };
      setFormData(resetData);
      setErrors({});
      setIsDirty(false);
    },
    [initialData]
  );

  // Reset form when openTrigger changes (for dialog forms)
  useEffect(() => {
    if (resetOnOpen && openTrigger) {
      resetForm();
    }
  }, [openTrigger, resetForm, resetOnOpen]);

  const updateField = useCallback(
    <K extends keyof T>(field: K, value: T[K]) => {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));

      setIsDirty(true);

      // Clear error for this field when it's updated
      if (errors[field]) {
        setErrors((prev) => ({
          ...prev,
          [field]: undefined,
        }));
      }
    },
    [errors]
  );

  const updateFields = useCallback((updates: Partial<T>) => {
    setFormData((prev) => ({
      ...prev,
      ...updates,
    }));

    setIsDirty(true);

    // Clear errors for updated fields
    const updatedFields = Object.keys(updates) as (keyof T)[];
    setErrors((prev) => {
      const newErrors = { ...prev };
      updatedFields.forEach((field) => {
        if (newErrors[field]) {
          delete newErrors[field];
        }
      });
      return newErrors;
    });
  }, []);

  const validateField = useCallback(
    (field: keyof T): boolean => {
      const fieldRules = validationRules.filter((rule) => rule.field === field);

      for (const rule of fieldRules) {
        if (!rule.validate(formData[field], formData)) {
          setErrors((prev) => ({
            ...prev,
            [field]: rule.errorMessage,
          }));
          return false;
        }
      }

      // Clear error if validation passes
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });

      return true;
    },
    [formData, validationRules]
  );

  const validateForm = useCallback((): boolean => {
    const newErrors: Partial<Record<keyof T, string>> = {};

    for (const rule of validationRules) {
      if (!rule.validate(formData[rule.field], formData)) {
        newErrors[rule.field] = rule.errorMessage;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, validationRules]);

  const clearErrors = useCallback(() => {
    setErrors({});
  }, []);

  const setError = useCallback((field: keyof T, error: string) => {
    setErrors((prev) => ({
      ...prev,
      [field]: error,
    }));
  }, []);

  const setFormDataDirectly = useCallback((data: T) => {
    setFormData(data);
    setErrors({});
    setIsDirty(true);
  }, []);

  const isValid =
    Object.keys(errors).length === 0 &&
    (validationRules.length === 0 ||
      validationRules.every((rule) => rule.validate(formData[rule.field], formData)));

  return {
    formData,
    errors,
    isValid,
    isDirty,
    updateField,
    updateFields,
    validateForm,
    validateField,
    resetForm,
    setFormData: setFormDataDirectly,
    clearErrors,
    setError,
  };
}
